import { Component, Inject, OnInit } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { CodeServerConfig } from "src/app/core/models/codeServer";

@Component({
  selector: "app-code-server-form",
  templateUrl: "./code-server-form.component.html",
  styleUrl: "./code-server-form.component.scss",
})
export class CodeServerFormComponent implements OnInit {
  codeServerForm: FormGroup;
  cpuOptions = [
    { value: "1000m/2000m", label: "1000m/2000m" },
    { value: "1500m/2000m", label: "1500m/2000m" },
    { value: "2000m/4000m", label: "2000m/4000m" },
  ];

  memoryOptions = [
    { value: "1Gi/2Gi", label: "1Gi/2Gi" },
    { value: "2Gi/4Gi", label: "2Gi/4Gi" },
    { value: '4Gi/8Gi', label: '4Gi/8Gi' },
  ];

  gpuOptions = [
    { value: '1/1', label: '1/1' }
  ];

  constructor(
    public dialog: MatDialogRef<CodeServerFormComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {},
    private fb: FormBuilder
  ) {
    this.codeServerForm = this.fb.group({
      password: ["", Validators.required],
      cpu: ['1000m/2000m', [Validators.required, this.cpuValidator()]],
      memory: ['1Gi/2Gi', [Validators.required, this.memoryValidator()]],
      gpu: ["1/1", [this.gpuValidator()]],
    });
  }

  ngOnInit(): void { }

  onClose() {
    this.dialog.close({ status: false, data: null });
  }

  onSubmit() {
    this.dialog.close({ status: true, data: null });
    const cpuValues = this.codeServerForm.value.cpu.split("/");
    const memoryValues = this.codeServerForm.value.memory.split("/");
    const gpuValues = (this.codeServerForm.value.gpu && this.codeServerForm.value.gpu !== "" && this.codeServerForm.value.gpu != null
    ) ? this.codeServerForm.value.gpu.split("/") : ["", ""];

    const config: CodeServerConfig = {
      Password: this.codeServerForm.value.password || undefined,
      Resources: {
        Requests: {
          Cpu: cpuValues[0],
          Memory: memoryValues[0],
          Gpu: gpuValues[0],
        },
        Limits: {
          Cpu: cpuValues[1],
          Memory: memoryValues[1],
          Gpu: gpuValues[1],
        },
      },
    };
    this.dialog.close({ status: true, data: config });
  }

  gpuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      const regex = /^(\d+)\/(\d+)$/;   // only integers
      const match = regex.exec(value);
      if (!match) return { invalidGpu: true };

      const left = parseInt(match[1], 10);
      const right = parseInt(match[2], 10);
      return left < right || left == right ? null : { invalidGpu: true };
    };
  }

  cpuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      const regex = /^(\d+)m\/(\d+)m$/;   // only integers + 'm'
      const match = regex.exec(value);
      if (!match) return { invalidCpu: true };

      const left = parseInt(match[1], 10);
      const right = parseInt(match[2], 10);
      return left < right || left == right ? null : { invalidCpu: true };
    };
  }

  // Memory Validator: e.g. 512Mi/1Gi
  memoryValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      // Require same unit on both sides (either Mi/Mi or Gi/Gi)
      const regex = /^(\d+)(Mi|Gi)\/(\d+)(Mi|Gi)$/;
      const match = regex.exec(value);
      if (!match) return { invalidMemory: true };

      const leftVal = parseInt(match[1], 10);
      const leftUnit = match[2];
      const rightVal = parseInt(match[3], 10);
      const rightUnit = match[4];

      // units must match (Mi with Mi or Gi with Gi)
      if (leftUnit !== rightUnit) return { invalidMemory: true };

      // check left < right
      return leftVal < rightVal || leftVal == rightVal ? null : { invalidMemory: true };
    };
  }
}
